import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import useAxios from '../api/axios';
import useAuth from '../hooks/useAuth';

const toLocalDateTimeInputValue = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');

  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

const createDefaultFilters = () => {
  const endDate = new Date();
  const startDate = new Date(endDate.getTime() - (60 * 60 * 1000));

  return {
    startAt: toLocalDateTimeInputValue(startDate),
    endAt: toLocalDateTimeInputValue(endDate),
    userQuery: '',
    competitionQuery: '',
  };
};

const formatDateTime = (value) => {
  if (!value) {
    return 'Ei tiedossa';
  }

  const parsedDate = new Date(value);
  if (Number.isNaN(parsedDate.getTime())) {
    return value;
  }

  return parsedDate.toLocaleString('fi-FI', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
};

const formatMetadata = (metadata) => {
  if (!metadata || typeof metadata !== 'object') {
    return 'Ei metadataa';
  }

  return JSON.stringify(metadata, null, 2);
};

function AuditLogs() {
  const api = useAxios();
  const { auth } = useAuth();
  const isAdminUser = Boolean(auth?.user?.isAdmin);
  const initialFilters = useMemo(() => createDefaultFilters(), []);

  const [filters, setFilters] = useState(initialFilters);
  const [logs, setLogs] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const [loading, setLoading] = useState(false);

  const loadLogs = useCallback(async (nextFilters) => {
    setLoading(true);

    try {
      const response = await api.post(
        '/getAuditLogs.php',
        JSON.stringify(nextFilters),
        { headers: { 'Content-Type': 'application/json' } }
      );

      const receivedLogs = Array.isArray(response?.data?.logs) ? response.data.logs : [];
      const receivedFilters = response?.data?.filters && typeof response.data.filters === 'object'
        ? {
            startAt: String(response.data.filters.startAt || nextFilters.startAt || ''),
            endAt: String(response.data.filters.endAt || nextFilters.endAt || ''),
            userQuery: String(response.data.filters.userQuery || nextFilters.userQuery || ''),
            competitionQuery: String(response.data.filters.competitionQuery || nextFilters.competitionQuery || ''),
          }
        : nextFilters;

      setLogs(receivedLogs);
      setAppliedFilters(receivedFilters);
    } catch (err) {
      const message = err?.response?.data?.message || err?.response?.data?.msg;
      toast.error(message || 'Audit-lokien haku epäonnistui.');
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    if (!isAdminUser) {
      return;
    }

    loadLogs(initialFilters);
  }, [initialFilters, isAdminUser, loadLogs]);

  const handleFilterChange = (field) => (event) => {
    const nextValue = event?.target?.value ?? '';
    setFilters((previousFilters) => ({
      ...previousFilters,
      [field]: nextValue,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await loadLogs(filters);
  };

  const handleReset = async () => {
    const nextFilters = createDefaultFilters();
    setFilters(nextFilters);
    await loadLogs(nextFilters);
  };

  if (!isAdminUser) {
    return (
      <div className="mx-auto max-w-4xl px-4 pb-10 pt-24">
        <div className="rounded-[28px] border border-white/10 bg-slate-900/70 p-8 text-center shadow-2xl shadow-slate-950/30 backdrop-blur">
          <p className="text-sm font-semibold uppercase tracking-[0.35em] text-cyan-300">Hallinto</p>
          <h1 className="mt-2 text-3xl font-black text-white">Audit Logs</h1>
          <p className="mt-4 text-sm text-slate-300">Tama sivu vaatii admin-oikeudet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 pb-10 pt-24">
      <div className="mb-6 rounded-[28px] border border-white/10 bg-slate-900/70 p-5 shadow-2xl shadow-slate-950/30 backdrop-blur">
        <p className="text-sm font-semibold uppercase tracking-[0.35em] text-cyan-300">Hallinto</p>
        <h1 className="mt-2 text-3xl font-black text-white md:text-4xl">Audit Logs</h1>
        <p className="mt-2 text-sm text-slate-300">
          Näytetään audit-lokit oletuksena viimeksi kuluneelta tunnilta. Voit suodattaa lokit ajan, käyttäjän tai kilpailun perusteella.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="mb-6 rounded-[28px] border border-white/10 bg-slate-900/70 p-5 shadow-2xl shadow-slate-950/30 backdrop-blur">
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <label className="block text-sm text-slate-200">
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-cyan-300">Alku</span>
            <input
              type="datetime-local"
              value={filters.startAt}
              onChange={handleFilterChange('startAt')}
              className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white outline-none transition focus:border-cyan-300"
            />
          </label>

          <label className="block text-sm text-slate-200">
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-cyan-300">Loppu</span>
            <input
              type="datetime-local"
              value={filters.endAt}
              onChange={handleFilterChange('endAt')}
              className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white outline-none transition focus:border-cyan-300"
            />
          </label>

          <label className="block text-sm text-slate-200">
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-cyan-300">Käyttäjä</span>
            <input
              type="text"
              value={filters.userQuery}
              onChange={handleFilterChange('userQuery')}
              placeholder="ID tai nimi"
              className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white outline-none transition placeholder:text-slate-500 focus:border-cyan-300"
            />
          </label>

          <label className="block text-sm text-slate-200">
            <span className="text-xs font-semibold uppercase tracking-[0.25em] text-cyan-300">Kilpailu</span>
            <input
              type="text"
              value={filters.competitionQuery}
              onChange={handleFilterChange('competitionQuery')}
              placeholder="ID tai nimi"
              className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm text-white outline-none transition placeholder:text-slate-500 focus:border-cyan-300"
            />
          </label>
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <button
            type="submit"
            disabled={loading}
            className="rounded-full border border-cyan-300/40 bg-cyan-300/10 px-5 py-2 text-sm font-semibold text-cyan-100 transition hover:bg-cyan-300/20 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {loading ? 'Haetaan...' : 'Hae lokit'}
          </button>
          <button
            type="button"
            onClick={handleReset}
            disabled={loading}
            className="rounded-full border border-white/10 bg-white/5 px-5 py-2 text-sm font-semibold text-slate-100 transition hover:bg-white/10 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Viimeinen tunti
          </button>
        </div>
      </form>

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 text-sm text-slate-300">
        <p>
          Näytetään {logs.length} lokiriviä aikaväliltä {formatDateTime(appliedFilters.startAt)} - {formatDateTime(appliedFilters.endAt)}.
        </p>
      </div>

      {loading ? (
        <div className="rounded-2xl border border-dashed border-white/15 bg-white/5 px-6 py-10 text-center text-slate-300">
          <p className="text-lg font-semibold text-white">Ladataan audit-lokeja...</p>
        </div>
      ) : logs.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/15 bg-white/5 px-6 py-10 text-center text-slate-300">
          <p className="text-lg font-semibold text-white">Valituilla suodattimilla ei löytynyt audit-lokeja.</p>
        </div>
      ) : (
        <div className="overflow-hidden rounded-[28px] border border-white/10 bg-slate-900/70 shadow-2xl shadow-slate-950/30 backdrop-blur">
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm text-slate-200">
              <thead className="bg-white/5 text-xs uppercase tracking-[0.2em] text-cyan-300">
                <tr>
                  <th className="px-4 py-4">Aika</th>
                  <th className="px-4 py-4">Toiminto</th>
                  <th className="px-4 py-4">Käyttäjä</th>
                  <th className="px-4 py-4">Kilpailu</th>
                  <th className="px-4 py-4">Metodi</th>
                  <th className="px-4 py-4">Metadata</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((log) => {
                  const userLabel = log?.userName ? `${log.userName} (#${log.userID})` : `#${log?.userID ?? '-'}`;
                  const competitionLabel = log?.competitionName
                    ? `${log.competitionName}${log?.competitionID ? ` (#${log.competitionID})` : ''}`
                    : (log?.competitionID ? `#${log.competitionID}` : 'Ei kohdistettua kilpailua');

                  return (
                    <tr key={log.auditLogID} className="border-t border-white/10 align-top">
                      <td className="px-4 py-4 font-medium text-white">{formatDateTime(log.createdAt)}</td>
                      <td className="px-4 py-4 font-semibold text-cyan-200">{log.action}</td>
                      <td className="px-4 py-4">{userLabel}</td>
                      <td className="px-4 py-4">{competitionLabel}</td>
                      <td className="px-4 py-4 uppercase text-slate-300">{log.requestMethod}</td>
                      <td className="px-4 py-4">
                        <pre className="max-w-xl whitespace-pre-wrap break-words rounded-2xl border border-white/10 bg-black/20 p-3 text-xs text-slate-200">
                          {formatMetadata(log.metadata)}
                        </pre>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export default AuditLogs;